/**
 * Service API pour les formulaires de recherche configurables
 */

import { HTTP_HYPER_ESPACE_BACKEND } from '@/services/http-common'

export class SearchFormApi {
  constructor() {
    // Utilisation de l'instance axios configurée du projet
    // qui gère automatiquement l'URL de base et l'authentification
    this.axios = HTTP_HYPER_ESPACE_BACKEND
  }

  /**
   * Récupère la configuration d'un formulaire de recherche
   * @param {Object} params - Paramètres de la requête
   * @param {string} params.businessDomain - Domaine métier (immo, vo, etc.)
   * @param {string} params.route - Route de la page
   * @param {Object} params.userContext - Contexte utilisateur (id, metier, etc.)
   * @param {Object} params.additionalContext - Contexte additionnel
   * @returns {Promise<Object>} Configuration du formulaire
   */
  async getSearchFormConfig({
    businessDomain,
    route,
    userContext = {},
    additionalContext = {}
  }) {
    try {
      const payload = {
        businessDomain,
        route,
        userContext,
        additionalContext
      }
      
      const response = await this.axios.post('/api/search-form/config', payload)
      
      return response.data
    } catch (error) {
      console.error('Erreur lors du chargement de la configuration de recherche:', error)
      throw new Error(`Impossible de charger la configuration du formulaire: ${error.message}`)
    }
  }

  /**
   * Récupère les données pour une source de données spécifique
   * @param {Object} params - Paramètres de la requête
   * @param {string} params.businessDomain - Domaine métier
   * @param {string} params.dataSourceCode - Code de la source de données
   * @param {Object} params.parameters - Paramètres dynamiques
   * @param {Object} params.userContext - Contexte utilisateur
   * @returns {Promise<Array>} Données de la source
   */
  async getDataSourceData({
    businessDomain,
    dataSourceCode,
    parameters = {},
    userContext = {}
  }) {
    try {
      const response = await this.axios.post('/api/search-form/data-source', {
        businessDomain,
        dataSourceCode,
        parameters,
        userContext
      })

      // Le backend retourne { success: true, data: [...] }
      return response.data.success ? response.data.data : []
    } catch (error) {
      console.error('Erreur lors de la récupération des données de la source:', error)
      throw new Error(`Impossible de charger les données de la source ${dataSourceCode}: ${error.message}`)
    }
  }

  /**
   * Récupère les données pour un champ spécifique (alias de getDataSourceData)
   * @param {Object} params - Paramètres de la requête
   * @param {string} params.businessDomain - Domaine métier
   * @param {string} params.fieldName - Nom du champ
   * @param {string} params.dataSourceCode - Code de la source de données
   * @param {Object} params.parameters - Paramètres dynamiques (ex: marque pour modèles)
   * @param {Object} params.userContext - Contexte utilisateur
   * @returns {Promise<Object>} Données du champ { success: true, data: [...] }
   */
  async getFieldData({
    businessDomain,
    fieldName,
    dataSourceCode,
    parameters = {},
    userContext = {}
  }) {
    try {
      const data = await this.getDataSourceData({
        businessDomain,
        dataSourceCode,
        parameters,
        userContext
      })

      return { success: true, data }
    } catch (error) {
      console.error(`Erreur lors de la récupération des données pour le champ ${fieldName}:`, error)
      return { success: false, data: [], error: error.message }
    }
  }

  /**
   * Récupère les données pour plusieurs sources en parallèle
   * @param {Object} params - Paramètres de la requête
   * @param {string} params.businessDomain - Domaine métier
   * @param {Array<string>} params.dataSourceCodes - Codes des sources de données
   * @param {Object} params.parameters - Paramètres dynamiques
   * @param {Object} params.userContext - Contexte utilisateur
   * @returns {Promise<Object>} Données des sources indexées par code
   */
  async getMultipleDataSources({
    businessDomain,
    dataSourceCodes,
    parameters = {},
    userContext = {}
  }) {
    try {
      const promises = dataSourceCodes.map(async (code) => {
        const data = await this.getDataSourceData({
          businessDomain,
          dataSourceCode: code,
          parameters,
          userContext
        })
        return { code, data }
      })

      const results = await Promise.all(promises)
      
      // Convertir en objet indexé par code
      return results.reduce((acc, { code, data }) => {
        acc[code] = data
        return acc
      }, {})
    } catch (error) {
      console.error('Erreur lors de la récupération des données multiples:', error)
      throw error
    }
  }

  /**
   * Valide les paramètres de filtre selon la configuration
   * @param {Object} params - Paramètres de validation
   * @param {string} params.businessDomain - Domaine métier
   * @param {string} params.route - Route de la page
   * @param {Object} params.filters - Filtres à valider
   * @param {Object} params.userContext - Contexte utilisateur
   * @returns {Promise<Object>} Résultat de la validation
   */
  async validateFilters({
    businessDomain,
    route,
    filters,
    userContext = {}
  }) {
    try {
      const response = await this.axios.post('/api/search-form/validate', {
        businessDomain,
        route,
        filters,
        userContext
      })

      return response.data
    } catch (error) {
      console.error('Erreur lors de la validation des filtres:', error)
      throw new Error(`Impossible de valider les filtres: ${error.message}`)
    }
  }

  /**
   * Sauvegarde les filtres utilisateur
   * @param {Object} params - Paramètres de sauvegarde
   * @param {string} params.page - Page/route
   * @param {Object} params.filters - Filtres à sauvegarder
   * @param {Array} params.pinnedFilters - Filtres épinglés
   * @returns {Promise<Object>} Résultat de la sauvegarde
   */
  async saveFilters({ page, filters, pinnedFilters }) {
    try {
      const response = await this.axios.post('/api/saved-filters/save', {
        page,
        filters,
        pinnedFilters
      })

      return response.data
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des filtres:', error)
      throw new Error(`Impossible de sauvegarder les filtres: ${error.message}`)
    }
  }

  /**
   * Charge les filtres sauvegardés
   * @param {Object} params - Paramètres de chargement
   * @param {string} params.page - Page/route
   * @returns {Promise<Object>} Filtres sauvegardés
   */
  async loadFilters({ page }) {
    try {
      const response = await this.axios.get('/api/saved-filters/load', {
        params: { page }
      })

      return response.data
    } catch (error) {
      console.error('Erreur lors du chargement des filtres:', error)
      throw new Error(`Impossible de charger les filtres: ${error.message}`)
    }
  }

}

// Instance singleton
export const searchFormApi = new SearchFormApi()

// Export par défaut
export default searchFormApi
